import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { supabase } from "./supabase";

/**
 * Utility function to merge Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Convert string to URL-friendly slug
 */
export function slugify(text: string): string {
  const slovakDiacritics: Record<string, string> = {
    á: "a",
    ä: "a",
    č: "c",
    ď: "d",
    é: "e",
    í: "i",
    ľ: "l",
    ĺ: "l",
    ň: "n",
    ó: "o",
    ô: "o",
    ŕ: "r",
    š: "s",
    ť: "t",
    ú: "u",
    ý: "y",
    ž: "z",
    Á: "A",
    Ä: "A",
    Č: "C",
    Ď: "D",
    É: "E",
    Í: "I",
    Ľ: "L",
    Ĺ: "L",
    Ň: "N",
    Ó: "O",
    Ô: "O",
    Ŕ: "R",
    Š: "S",
    Ť: "T",
    Ú: "U",
    Ý: "Y",
    Ž: "Z",
  };

  return text
    .toLowerCase()
    .split("")
    .map((char) => slovakDiacritics[char] || char)
    .join("")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

/**
 * Get avatar URL - handles both full URLs and Supabase Storage paths
 * @param avatarUrl - The avatar URL from database (can be full URL or storage path)
 * @returns The public URL for the avatar image, or null if not available
 */
export function getAvatarUrl(
  avatarUrl: string | null | undefined,
): string | null {
  if (!avatarUrl) {
    return null;
  }

  // Trim whitespace
  const trimmedUrl = avatarUrl.trim();
  if (!trimmedUrl) {
    return null;
  }

  // If it's already a full URL (starts with http:// or https://), return as is
  if (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")) {
    return trimmedUrl;
  }

  // If it's a Supabase Storage path, convert to public URL
  // Format: "avatars/user-id.jpg" or "/avatars/user-id.jpg"
  const storagePath = trimmedUrl.startsWith("/")
    ? trimmedUrl.slice(1)
    : trimmedUrl;

  try {
    const { data } = supabase.storage.from("avatars").getPublicUrl(storagePath);
    return data.publicUrl;
  } catch (error) {
    console.error("Error getting public URL for avatar:", error);
    return null;
  }
}
